import {computed, inject, Injectable, signal} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {AuthService} from '../../auth.service';
import { LimitsService } from 'src/api';
import { firstValueFrom } from 'rxjs';

/**
 * Tracks maintenance mode from GET /limits 503 responses.
 * Kept separate from LimitsStateService so the app shell can gate the UI
 * without pulling limits popups and related UI into the initial bundle.
 */
@Injectable({
  providedIn: 'root',
})
export class MaintenanceStateService {
  private readonly limitsService = inject(LimitsService);
  private readonly auth = inject(AuthService);

  private readonly _maintenanceModeEnabled = signal(false);
  private readonly _maintenanceModeUntil = signal<string | undefined>(undefined);

  readonly maintenanceModeEnabled = this._maintenanceModeEnabled.asReadonly();
  readonly maintenanceModeUntil = this._maintenanceModeUntil.asReadonly();
  readonly blockingMaintenanceActive = computed(
    () => this._maintenanceModeEnabled() && !this.auth.isStaff,
  );

  clear(): void {
    this._maintenanceModeEnabled.set(false);
    this._maintenanceModeUntil.set(undefined);
  }

  applyFromError(err: HttpErrorResponse): void {
    const until = typeof err.error === 'object' && err.error !== null
      ? (err.error as {until?: string}).until
      : undefined;
    this._maintenanceModeUntil.set(until);
    this._maintenanceModeEnabled.set(true);
  }

  isMaintenanceError(err: HttpErrorResponse): boolean {
    const body = err.error;
    return (
      err.status === 503
      && typeof body === 'object'
      && body !== null
      && body.error === 'Maintenance mode is enabled'
    );
  }

  async checkMaintenanceEnded(): Promise<void> {
    if (!this._maintenanceModeEnabled() || this.auth.isStaff) {
      return;
    }

    try {
      await firstValueFrom(this.limitsService.getLimits());
      this.clear();
      window.location.reload();
    } catch (err) {
      if (err instanceof HttpErrorResponse && this.isMaintenanceError(err)) {
        this.applyFromError(err);
      }
    }
  }
}
